import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import mixpanel from "mixpanel-browser";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { setSentryUserContext } from "@/lib/sentry";

import AuthProvider from "./components/AuthProvider";
import DevLogoutButton from "./components/DevLogoutButton";
import ProtectedRoute from "./components/ProtectedRoute";
import Admins from "./features/admins/pages/Admins";
import AdminSettings from "./features/admins/pages/AdminSettings";
import CertifiedFounders from "./features/admins/pages/CertifiedFounders";
import CertifiedPush from "./features/admins/pages/CertifiedPush";
import Applicants from "./features/applicants/pages/Applicants";
import Clients from "./features/clients/pages/Clients";
import Cohorts from "./features/cohorts/pages/Cohorts";
import { MobileChatHistoryProvider } from "./features/copilot/context/MobileChatHistoryContext";
import CoPilot from "./features/copilot/pages/CoPilot";
import { StaceyLibraryPage } from "./features/copilot/pages/StaceyLibraryPage";
import CertifiedFounderRoute from "./features/founders/components/CertifiedFounderRoute";
import LicenseeSubscriptionGate from "./features/founders/components/LicenseeSubscriptionGate";
import NonLegacyFounderRoute from "./features/founders/components/NonLegacyFounderRoute";
import FounderDashboard from "./features/founders/pages/FounderDashboard";
import FounderProfile from "./features/founders/pages/FounderProfile";
import FounderPublicProfilePage from "./features/founders/pages/FounderPublicProfilePage";
import Founders from "./features/founders/pages/Founders";
import MentorFounders from "./features/founders/pages/MentorFounders";
import Welcome from "./features/founders/pages/Welcome";
import Invoices from "./features/invoices/pages/Invoices";
import Leads from "./features/leads/pages/Leads";
import MentorDashboard from "./features/mentors/pages/MentorDashboard";
import MentorPublicProfilePage from "./features/mentors/pages/MentorPublicProfilePage";
import Mentors from "./features/mentors/pages/Mentors";
import MentorSettings from "./features/mentors/pages/MentorSettings";
import Payouts from "./features/payouts/pages/Payouts";
import api from "./lib/axios";
import DesignSystem from "./pages/DesignSystem";
import NotFound from "./pages/NotFound";
import UnsupportedRole from "./pages/UnsupportedRole";
import {
  setupAxiosInterceptors,
  setupGlobalErrorHandling,
} from "./utils/error-handling";
import { getUserRole } from "./utils/users";

const queryClient = new QueryClient();
setupGlobalErrorHandling(queryClient);
setupAxiosInterceptors(api);

const HomeRoute = () => {
  const { user, loginWithRedirect } = useAuth0();

  const userRole = getUserRole(user);

  // If no user role, redirect to login and don't render anything
  useEffect(() => {
    if (userRole === null) {
      loginWithRedirect();
    }

    // Set Mixpanel user identification when user is available
    if (user) {
      mixpanel.identify(user.sub);
      mixpanel.people.set({
        $name: user.name,
        $email: user.email,
        role: userRole,
      });

      // Set Sentry user context
      setSentryUserContext({ id: user.sub!, email: user.email });
    }
  }, [userRole, loginWithRedirect, user]);

  // Don't render anything if user role is null
  if (userRole === null) {
    return null;
  }

  if (userRole === "licensee") {
    return (
      <ProtectedRoute allowedRoles={["licensee"]}>
        <LicenseeSubscriptionGate>
          <FounderDashboard />
        </LicenseeSubscriptionGate>
      </ProtectedRoute>
    );
  } else if (userRole === "mentor") {
    return (
      <ProtectedRoute allowedRoles={["mentor"]}>
        <MentorDashboard />
      </ProtectedRoute>
    );
  } else if (userRole === "admin") {
    return <Navigate to="/founders" replace />;
  }
  return <NotFound />;
};

const App = () => (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <MobileChatHistoryProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <DevLogoutButton />
            <Routes>
                <Route path="/" element={<HomeRoute />} />
                {/* Back-compat redirect from old Co-Pilot path */}
                <Route path="/co-pilot" element={<Navigate to="/chat" replace />} />
                <Route
                  path="/design-system"
                  element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                      <DesignSystem />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/chat"
                  element={
                    <ProtectedRoute allowedRoles={["licensee"]}>
                      <LicenseeSubscriptionGate>
                        <NonLegacyFounderRoute>
                          <CoPilot />
                        </NonLegacyFounderRoute>
                      </LicenseeSubscriptionGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stacey-library"
                  element={
                    <ProtectedRoute allowedRoles={["licensee"]}>
                      <LicenseeSubscriptionGate>
                        <NonLegacyFounderRoute>
                          <StaceyLibraryPage />
                        </NonLegacyFounderRoute>
                      </LicenseeSubscriptionGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/invoices"
                  element={
                    <ProtectedRoute allowedRoles={["licensee"]}>
                      <LicenseeSubscriptionGate>
                        <Invoices />
                      </LicenseeSubscriptionGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/clients"
                  element={
                    <ProtectedRoute allowedRoles={["licensee"]}>
                      <LicenseeSubscriptionGate>
                        <Clients />
                      </LicenseeSubscriptionGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads"
                  element={
                    <ProtectedRoute allowedRoles={["licensee"]}>
                      <LicenseeSubscriptionGate>
                        <Leads />
                      </LicenseeSubscriptionGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/founder/settings"
                  element={
                    <ProtectedRoute allowedRoles={["licensee"]}>
                      <LicenseeSubscriptionGate>
                        <FounderProfile />
                      </LicenseeSubscriptionGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/founder/profile"
                  element={
                    <ProtectedRoute allowedRoles={["licensee"]}>
                      <LicenseeSubscriptionGate>
                        <CertifiedFounderRoute>
                          <FounderPublicProfilePage />
                        </CertifiedFounderRoute>
                      </LicenseeSubscriptionGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-founders"
                  element={
                    <ProtectedRoute allowedRoles={["mentor"]}>
                      <MentorFounders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/mentor/settings"
                  element={
                    <ProtectedRoute allowedRoles={["mentor"]}>
                      <MentorSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/mentor/profile"
                  element={
                    <ProtectedRoute allowedRoles={["mentor"]}>
                      <MentorPublicProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admins"
                  element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                      <Admins />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin-settings"
                  element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                      <AdminSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/certified-push"
                  element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                      <CertifiedPush />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/certified-founders"
                  element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                      <CertifiedFounders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/mentors"
                  element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                      <Mentors />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cohorts"
                  element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                      <Cohorts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/founders"
                  element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                      <Founders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/applicants"
                  element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                      <Applicants />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payouts"
                  element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                      <Payouts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/welcome"
                  element={
                    <ProtectedRoute allowedRoles={["licensee"]}>
                      <Welcome />
                    </ProtectedRoute>
                  }
                />
                <Route path="/unsupported-role" element={<UnsupportedRole />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
          </TooltipProvider>
        </MobileChatHistoryProvider>
      </AuthProvider>
    </QueryClientProvider>
  </BrowserRouter>
);

export default App;
